<template>
    <DForm>
        <DFormRow
            :label="__('settings.zendesk_settings.form.api_url')"
            :is-required="true"
            :error="errorsData.api_url"
            :hint="__('settings.zendesk_settings.form.api_url_hint')"
        >
            <DInput
                id="zd_api_url"
                v-model="form.api_url"
                :placeholder="__('settings.zendesk_settings.form.api_url')"
                type="url"
                :is-valid="isFormFieldValid('api_url')"
                maxlength="255"
            />
        </DFormRow>
        <DFormRow
            :label="__('settings.zendesk_settings.form.api_username')"
            :is_required="true"
            :error="errorsData.api_username"
            :hint="__('settings.zendesk_settings.form.api_username_hint')"
        >
            <DInput
                id="zd_api_username"
                v-model="form.api_username"
                :placeholder="__('settings.zendesk_settings.form.api_username')"
                type="text"
                :is-valid="isFormFieldValid('api_username')"
                maxlength="255"
            />
        </DFormRow>
        <DFormRow
            :label="__('settings.zendesk_settings.form.api_key')"
            :is_required="true"
            :error="errorsData.api_key"
            :hint="__('settings.zendesk_settings.form.api_key_hint')"
        >
            <DInput
                id="zd_api_key"
                v-model="form.api_key"
                :placeholder="__('settings.zendesk_settings.form.api_key')"
                type="password"
                :is-valid="isFormFieldValid('api_key')"
                maxlength="255"
            />
        </DFormRow>
        <DFormRow
            :label="__('settings.zendesk_settings.form.contract_id')"
            :is-required="true"
            :error="errorsData.contract_id_field_id"
            :hint="__('settings.zendesk_settings.form.contract_id_hint')"
        >
            <DInput
                id="zd_contract_id_field_id"
                v-model="form.contract_id_field_id"
                :placeholder="__('settings.zendesk_settings.form.contract_id')"
                type="text"
                :is-valid="isFormFieldValid('contract_id_field_id')"
                maxlength="64"
            />
        </DFormRow>
        <DFormRow
            :label="__('settings.zendesk_settings.form.order_id')"
            :is-required="true"
            :error="errorsData.order_number_field_id"
            :hint="__('settings.zendesk_settings.form.order_id_hint')"
        >
            <DInput
                id="zd_order_number_field_id"
                v-model="form.order_number_field_id"
                :placeholder="__('settings.zendesk_settings.form.order_id')"
                type="text"
                :is-valid="isFormFieldValid('order_number_field_id')"
                maxlength="64"
            />
        </DFormRow>
        <DFormRow
            :label="__('settings.zendesk_settings.form.customer_id_field_identifier')"
            :is-required="true"
            :error="errorsData.customer_id_field_identifier"
            :hint="__('settings.zendesk_settings.form.customer_id_field_identifier_hint')"
        >
            <DInput
                id="zd_customer_id_field_identifier"
                v-model="form.customer_id_field_identifier"
                :placeholder="__('settings.zendesk_settings.form.customer_id_field_identifier')"
                type="text"
                :is-valid="isFormFieldValid('customer_id_field_identifier')"
                maxlength="64"
            />
        </DFormRow>
        <DFormRow
            :label="__('settings.zendesk_settings.form.invoice_id_field_identifier')"
            :is-required="true"
            :error="errorsData.invoice_id_field_identifier"
            :hint="__('settings.zendesk_settings.form.invoice_id_field_identifier_hint')"
        >
            <DInput
                id="zd_invoice_id_field_identifier"
                v-model="form.invoice_id_field_identifier"
                :placeholder="__('settings.zendesk_settings.form.invoice_id_field_identifier')"
                type="text"
                :is-valid="isFormFieldValid('invoice_id_field_identifier')"
                maxlength="64"
            />
        </DFormRow>
        <DRow>
            <DCol sm="4" />
            <DCol sm="4">
                <div
                    v-if="connectionStatus === 'successful'"
                    class="green-text"
                >
                    {{ __('settings.connections.connection_successful') }}
                </div>
                <div
                    v-if="connectionStatus === 'unsuccessful'"
                    class="red-text"
                >
                    {{ __('settings.connections.connection_unsuccessful') }}
                </div>
            </DCol>
            <DCol
                sm="4"
                class="check-database-connection-btn"
            >
                <DButton
                    icon="lan-connect"
                    :label="__('settings.connections.check_connection')"
                    :disabled="isInLoadingState"
                    @click="checkConnection()"
                />
            </DCol>
        </DRow>
        <DFormControls
            :dirty="form.isDirty"
            @submit="submitForm()"
        />
        <DConnectionErrorModal
            :show-modal="connectionException !== null"
            :exception="connectionException"
            @close-modal="closeErrorCheckModal"
        />
    </DForm>
</template>
<script setup>
import { ref, onUpdated } from 'vue'
import { route } from '~@/Composables/route'
import { useForm } from '@inertiajs/vue3'

import DConnectionErrorModal from '~@/Components/Common/DConnectionErrorModal'

const props = defineProps({
    zenDeskSettings: {
        type: Object,
        default: () => { return {} }
    },
    errors: {
        type: Object,
        default: () => { return {} }
    }
})

const form = useForm({
    api_url: props.zenDeskSettings?.api_url,
    api_username: props.zenDeskSettings?.api_username,
    api_key: props.zenDeskSettings?.api_key,
    contract_id_field_id: props.zenDeskSettings?.contract_id_field_id,
    order_number_field_id: props.zenDeskSettings?.order_number_field_id,
    customer_id_field_identifier: props.zenDeskSettings?.customer_id_field_identifier,
    invoice_id_field_identifier: props.zenDeskSettings?.invoice_id_field_identifier
})

const emit = defineEmits(['submit-form'])

const connectionStatus = ref('')
const credentialsAreValid = ref(false)
const connectionException = ref(null)
const apiError = ref(null)
const errorsData = ref(props.errors)

onUpdated(() => {
    errorsData.value = props.errors
})

const checkConnection = () => {
    errorsData.value = {}
    connectionException.value = null
    window.axios.post(route('settings.zen-desk-settings.check', form))
        .then(function () {
            connectionStatus.value = 'successful'
            credentialsAreValid.value = true
        })
        .catch(function (error) {
            connectionStatus.value = 'unsuccessful'
            credentialsAreValid.value = false
            if (error.response.data.api_error !== undefined) {
                apiError.value = error.response.data.api_error
            }

            if (error.response.data.errors !== undefined) {
                errorsData.value = error.response.data.errors
                for (const key in errorsData.value) {
                    if (typeof errorsData.value[key] === 'object') {
                        errorsData.value[key] = errorsData.value[key][0]
                    }
                }
            }

            if (error?.response.data.exception !== undefined) {
                connectionException.value = error.response.data.exception
            }
        })
}

const submitForm = () => {
    emit('submit-form', form)
}

const closeErrorCheckModal = () => {
    connectionException.value = null
    apiError.value = null
}
</script>

<script>
export default {
    name: 'ZenDeskForm'
}
</script>

<style lang="scss" scoped>
.check-database-connection-btn {
    text-align: right;
    padding-bottom: 12px;
}
.green-text {
    color: green;
}
.red-text {
    color: red;
}
</style>
